import { useState } from "react";
import { useMemo } from "react";
import { TABLE_CONFIG } from "./constants";
import type { CategoryData, TableRowData } from "./types";

// 日付グループの処理
export const useDataGrouping = (data: TableRowData[]) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );

  const groupedData = useMemo(() => {
    const groups: Record<string, TableRowData[]> = {};
    data.forEach((row) => {
      if (!groups[row.anchorDate]) {
        groups[row.anchorDate] = [];
      }
      groups[row.anchorDate].push(row);
    });

    // 初期状態ですべての日付グループを展開状態にする
    const initialExpandedState: Record<string, boolean> = {};
    Object.keys(groups).forEach((date) => {
      initialExpandedState[date] = true;
    });

    // 初期状態を設定（初回レンダリング時のみ）
    setExpandedGroups((prev) => {
      if (Object.keys(prev).length === 0) {
        return initialExpandedState;
      }
      return prev;
    });

    return groups;
  }, [data]);

  const toggleGroupExpansion = (date: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  return { groupedData, expandedGroups, toggleGroupExpansion };
};

// カテゴリーデータの抽出
export const useCategories = (data: TableRowData[]) => {
  return useMemo(() => {
    const largeCategoriesMap = new Map<
      string,
      { code: string; name: string; mediumCategories: Map<string, string> }
    >();

    data.forEach((row) => {
      row.columns.forEach((column) => {
        if (!largeCategoriesMap.has(column.largeCategoryCode)) {
          largeCategoriesMap.set(column.largeCategoryCode, {
            code: column.largeCategoryCode,
            name: column.largeCategoryName,
            mediumCategories: new Map(),
          });
        }

        const largeCategory = largeCategoriesMap.get(column.largeCategoryCode);
        if (
          largeCategory &&
          !largeCategory.mediumCategories.has(column.mediumCategoryCode)
        ) {
          largeCategory.mediumCategories.set(
            column.mediumCategoryCode,
            column.mediumCategoryName,
          );
        }
      });
    });

    return Array.from(largeCategoriesMap.values()).map((largeCategory) => ({
      code: largeCategory.code,
      name: largeCategory.name,
      mediumCategories: Array.from(
        largeCategory.mediumCategories.entries(),
      ).map(([code, name]) => ({ code, name })),
    }));
  }, [data]);
};

// ホバー状態の管理
export const useHoverState = () => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [hoveredColIndex, setHoveredColIndex] = useState<number | null>(null);

  const handleCellMouseEnter = (rowIndex: number, colIndex: number) => {
    setHoveredRowIndex(rowIndex);
    setHoveredColIndex(colIndex);
  };

  const handleCellMouseLeave = () => {
    setHoveredRowIndex(null);
    setHoveredColIndex(null);
  };

  return {
    hoveredRowIndex,
    setHoveredRowIndex,
    hoveredColIndex,
    setHoveredColIndex,
    handleCellMouseEnter,
    handleCellMouseLeave,
  };
};

// カラムインデックスの計算を一元化
export const useColumnIndexer = (categories: CategoryData[]) => {
  return useMemo(() => {
    // 指定された大カテゴリー・中カテゴリーのインデックスを計算するヘルパー関数
    const getCategoryColumnIndex = (
      largeCategory: CategoryData,
      mediumCategory: { code: string; name: string },
      categories: CategoryData[],
    ): number => {
      const fixedColumnCount = TABLE_CONFIG.FIXED_COLUMN_COUNT;
      const largeCatIndex = categories.findIndex(
        (cat) => cat.code === largeCategory.code,
      );

      if (largeCatIndex === -1) return -1;

      const mediumCatIndex = largeCategory.mediumCategories.findIndex(
        (med) => med.code === mediumCategory.code,
      );

      if (mediumCatIndex === -1) return -1;

      // 固定カラム数 + 前の大カテゴリーに含まれる中カテゴリーの数 + 現在の中カテゴリーのインデックス
      return (
        fixedColumnCount +
        categories
          .slice(0, largeCatIndex)
          .reduce((acc, cat) => acc + cat.mediumCategories.length, 0) +
        mediumCatIndex
      );
    };

    return {
      // 固定カラム数
      fixedColumnCount: TABLE_CONFIG.FIXED_COLUMN_COUNT,

      // 固定カラムのインデックスを取得
      getFixedColumnIndex: (id: "person" | "progress"): number => {
        return (
          TABLE_CONFIG.FIXED_COLUMNS.find((col) => col.id === id)?.index ?? -1
        );
      },

      // カテゴリーカラムのインデックスを計算
      getCategoryColumnIndex: (
        largeCategory: CategoryData,
        mediumCategory: { code: string; name: string },
      ): number => {
        return getCategoryColumnIndex(
          largeCategory,
          mediumCategory,
          categories,
        );
      },

      // 総カラム数を計算
      getTotalColumnCount: (): number => {
        return (
          TABLE_CONFIG.FIXED_COLUMN_COUNT +
          categories.reduce((acc, cat) => acc + cat.mediumCategories.length, 0)
        );
      },
    };
  }, [categories]);
};
